/**
 * Family Tree Backup Export — owner/admin only.
 *
 * Read-only snapshot of the current viewer's family graph. Pulls every member
 * row, every memory row, and every photo / comment row owned by the viewer
 * and writes them into a single downloadable JSON file together with an
 * export-date marker and a project/build identifier so the file is self-
 * describing for future imports / audits.
 *
 * SAFETY:
 *   - Pure SELECT queries. Never mutates anything.
 *   - Does not touch billing, auth, RLS, schema, uploads, or tree rendering.
 *   - Best-effort per table — a missing optional table (e.g. comments) just
 *     records `null` for that section instead of throwing the whole export.
 *   - Returns a single JSON Blob and triggers a browser download.
 */
import { supabase } from './supabase';

const PROJECT_MARKER = 'kinscircle';
const EXPORT_FORMAT_VERSION = 1;

interface BackupBundle {
  format: string;
  format_version: number;
  project: string;
  build: string;
  exported_at: string;
  exported_by: {
    user_id: string;
    email: string | null;
  };
  counts: {
    members: number;
    memories: number;
    photos: number;
    comments: number;
  };
  members: any[];
  memories: any[];
  photos: any[] | null;
  comments: any[] | null;
}

async function safeSelect(
  table: string,
  ownerCol: string,
  ownerId: string,
): Promise<any[] | null> {
  try {
    const { data, error } = await supabase.from(table).select('*').eq(ownerCol, ownerId);
    if (error) {
      console.warn(`[backup] select ${table} failed:`, error.message);
      return null;
    }
    return data ?? [];
  } catch (err) {
    console.warn(`[backup] select ${table} threw:`, err);
    return null;
  }
}

export async function exportFamilyTreeBackup(opts: {
  userId: string;
  email: string | null;
}): Promise<{ ok: boolean; filename?: string; error?: string }> {
  const { userId, email } = opts;
  if (!userId) return { ok: false, error: 'Missing user id' };

  // Members — owner_user_id scopes the entire visible tree for this owner.
  const members = await safeSelect('family_graph_members', 'owner_user_id', userId);
  if (members === null) {
    return { ok: false, error: 'Could not read family members. Try again.' };
  }

  // Memories — also keyed by owner_user_id.
  const memories = (await safeSelect('family_graph_memories', 'owner_user_id', userId)) ?? [];

  // Photos and comments tables may or may not exist depending on schema age;
  // gracefully record `null` if the table is absent so the export still
  // succeeds.
  const photos = await safeSelect('family_graph_photos', 'owner_user_id', userId);
  const comments = await safeSelect('family_graph_memory_comments', 'owner_user_id', userId);

  // Compute per-member memory counts so importers / humans can sanity-check
  // counts without re-aggregating the memories array.
  const memoryCountByMember: Record<string, number> = {};
  for (const m of memories) {
    if (m && m.member_id) {
      memoryCountByMember[m.member_id] = (memoryCountByMember[m.member_id] || 0) + 1;
    }
  }

  // Project a slim, audit-friendly view of each member that highlights the
  // exact fields the spec calls out (parent_ids, spouse_id, override, dob,
  // notes, memory count, timestamps) while still embedding the full raw row
  // so nothing is silently dropped.
  const enrichedMembers = (members ?? []).map((m: any) => ({
    id: m.id,
    name: m.name ?? null,
    relationship: m.relationship ?? null,
    relationship_override: m.relationship_override ?? null,
    side: m.side ?? null,
    parent_ids: Array.isArray(m.parent_ids) ? m.parent_ids : [],
    spouse_id: m.spouse_id ?? null,
    birthday: m.birthday ?? m.dob ?? null,
    notes: m.notes ?? null,
    photo_url: m.photo_url ?? null,
    claimed_by_user_id: m.claimed_by_user_id ?? null,
    memory_count: memoryCountByMember[m.id] || 0,
    created_at: m.created_at ?? null,
    updated_at: m.updated_at ?? null,
    raw: m,
  }));

  const bundle: BackupBundle = {
    format: 'kinscircle.family-tree-backup',
    format_version: EXPORT_FORMAT_VERSION,
    project: PROJECT_MARKER,
    build:
      (typeof window !== 'undefined' && (window as any).__KC_BUILD__) ||
      import.meta.env?.VITE_BUILD_ID ||
      'dev',
    exported_at: new Date().toISOString(),
    exported_by: { user_id: userId, email: email ?? null },
    counts: {
      members: enrichedMembers.length,
      memories: memories.length,
      photos: photos?.length ?? 0,
      comments: comments?.length ?? 0,
    },
    members: enrichedMembers,
    memories,
    photos,
    comments,
  };

  // Trigger browser download.
  try {
    const json = JSON.stringify(bundle, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `kinscircle-family-tree-backup-${stamp}.json`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { ok: true, filename };
  } catch (err: any) {
    return { ok: false, error: err?.message || 'Failed to write backup file' };
  }
}
